import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api, extractError, BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import {
  ShieldCheck, BadgeCheck, X, Check, Loader2, FileText, Search,
  UserCircle2, MapPin, Star, DollarSign, ExternalLink, Users, GraduationCap,
  Mail, Phone, Send, Copy, Trash2, RefreshCw, LinkIcon, Bell, KeyRound
} from "lucide-react";

const CORE = [
  { key: "identity_verified", label: "Identity" },
  { key: "background_check", label: "Background check" },
  { key: "child_abuse_registry", label: "Child abuse registry" },
  { key: "cpr_certification", label: "CPR" },
  { key: "references_verified", label: "References" },
  { key: "license_verified", label: "License" },
  { key: "insurance_verified", label: "Insurance" },
];

function StatCard({ label, value, tone = "sage", testId }) {
  const bg = tone === "ochre" ? "bg-ochre-50" : "bg-sage-50";
  const text = tone === "ochre" ? "text-ochre-800" : "text-sage-800";
  return (
    <div className={`rounded-xl ${bg} p-5`} data-testid={testId}>
      <div className={`text-xs uppercase tracking-[0.18em] font-bold ${text}`}>{label}</div>
      <div className="text-3xl font-heading font-bold text-sage-900 mt-2">{value}</div>
    </div>
  );
}

export default function AdminDashboard() {
  const [tab, setTab] = useState("sitters"); // "sitters" | "families" | "invites"
  const [stats, setStats] = useState(null);
  const [sitters, setSitters] = useState([]);
  const [families, setFamilies] = useState([]);
  const [invites, setInvites] = useState([]);
  const [waitlist, setWaitlist] = useState({ results: [], top_zips: [] });
  const [filter, setFilter] = useState("pending");
  const [query, setQuery] = useState("");
  const [openId, setOpenId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);

  // Invite compose state
  const [inviteMode, setInviteMode] = useState("single"); // "single" | "batch"
  const [inviteRole, setInviteRole] = useState("family");
  const [singleEmail, setSingleEmail] = useState("");
  const [singleName, setSingleName] = useState("");
  const [singlePhone, setSinglePhone] = useState("");
  const [batchText, setBatchText] = useState("");
  const [inviteMessage, setInviteMessage] = useState("");
  const [inviting, setInviting] = useState(false);

  const load = async () => {
    try {
      const stCall = api.get("/admin/stats");
      const listCall = api.get("/admin/sitters", {
        params: filter === "all" ? {} : { verified: filter === "verified" },
      });
      const famCall = api.get("/admin/families");
      const invCall = api.get("/admin/invites");
      const wlCall = api.get("/admin/waitlist/coverage");
      const [{ data: s }, { data: l }, { data: f }, { data: inv }, { data: wl }] = await Promise.all([stCall, listCall, famCall, invCall, wlCall]);
      setStats(s);
      setSitters(l.results);
      setFamilies(f.results);
      setInvites(inv.results);
      setWaitlist({ results: wl.results || [], top_zips: wl.top_zips || [] });
    } catch (e) { toast.error(extractError(e)); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const openDetail = async (id) => {
    setOpenId(id); setDetail(null);
    try {
      const { data } = await api.get(`/admin/sitters/${id}`);
      setDetail(data);
    } catch (e) { toast.error(extractError(e)); setOpenId(null); }
  };

  const pingWaitlistEntry = async (entryId) => {
    try {
      const { data } = await api.post(`/admin/waitlist/coverage/${entryId}/ping-now`);
      toast.success(`Pinged ${data.count} famil${data.count === 1 ? "y" : "ies"} in this ZIP`);
      await load();
    } catch (e) { toast.error(extractError(e)); }
  };

  const toggle = async (sitterId, key, next) => {
    setBusy(true);
    try {
      const { data } = await api.put(`/admin/sitters/${sitterId}/verification`, { [key]: next });
      setDetail((d) => (d ? { ...d, verifications: data.verifications, verified: data.verified } : d));
      // Also update the row in the list
      setSitters((list) => list.map((s) => s.id === sitterId
        ? { ...s, verifications: data.verifications, verified: data.verified } : s));
      toast.success("Updated");
    } catch (e) { toast.error(extractError(e)); }
    finally { setBusy(false); }
  };

  const visible = sitters.filter((s) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return (s.name || "").toLowerCase().includes(q) || (s.user_email || "").toLowerCase().includes(q);
  });

  const visibleFamilies = families.filter((f) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return (f.name || "").toLowerCase().includes(q)
        || (f.email || "").toLowerCase().includes(q)
        || (f.parent_name || "").toLowerCase().includes(q)
        || (f.family_name || "").toLowerCase().includes(q)
        || (f.city || "").toLowerCase().includes(q)
        || (f.state || "").toLowerCase().includes(q);
  });

  const copyLink = (url) => {
    navigator.clipboard.writeText(url)
      .then(() => toast.success("Invite link copied"))
      .catch(() => toast.error("Copy failed — select and copy manually"));
  };

  const submitInvite = async (e) => {
    e.preventDefault();
    setInviting(true);
    try {
      if (inviteMode === "single") {
        if (!singleEmail && !singlePhone) {
          toast.error("Enter at least an email or phone");
          setInviting(false); return;
        }
        const { data } = await api.post("/admin/invites", {
          role: inviteRole,
          recipient_email: singleEmail || undefined,
          recipient_name: singleName || undefined,
          recipient_phone: singlePhone || undefined,
          message: inviteMessage || undefined,
        });
        toast.success("Invite created — link copied");
        copyLink(data.url);
        setSingleEmail(""); setSingleName(""); setSinglePhone("");
      } else {
        const { data } = await api.post("/admin/invites/batch", {
          role: inviteRole,
          recipients_text: batchText,
          message: inviteMessage || undefined,
        });
        toast.success(`${data.created_count} invites created${data.skipped_count ? `, ${data.skipped_count} skipped` : ""}`);
        setBatchText("");
      }
      setInviteMessage("");
      load();
    } catch (err) { toast.error(extractError(err)); }
    finally { setInviting(false); }
  };

  const resendInvite = async (id) => {
    try {
      await api.post(`/admin/invites/${id}/resend`);
      toast.success("Queued for re-delivery");
      load();
    } catch (err) { toast.error(extractError(err)); }
  };

  const revokeInvite = async (id) => {
    if (!window.confirm("Revoke this invite? The recipient will no longer be able to use it.")) return;
    try {
      await api.delete(`/admin/invites/${id}`);
      toast.success("Invite revoked");
      load();
    } catch (err) { toast.error(extractError(err)); }
  };

  const [purging, setPurging] = useState(false);
  const purgeTestData = async () => {
    const msg = "This will PERMANENTLY delete every family and sitter account whose email is on a test domain (@test.com, @t.com). Real users are safe.\n\nContinue?";
    if (!window.confirm(msg)) return;
    setPurging(true);
    try {
      const { data } = await api.post("/admin/purge-test-fixtures");
      const n = data.purged_users || 0;
      if (n === 0) {
        toast.success("No test fixtures found — database is already clean.");
      } else {
        toast.success(`Purged ${n} test accounts and all their data.`);
      }
      load();
    } catch (err) { toast.error(extractError(err)); }
    finally { setPurging(false); }
  };

  const [seeding, setSeeding] = useState(false);
  const seedCurated = async () => {
    if (!window.confirm("Add curated sample sitters (Maya R. + Jordan K.) to your live database?\n\nSafe to click multiple times — updates existing records.")) return;
    setSeeding(true);
    try {
      const { data } = await api.post("/admin/seed-curated-sitters");
      const actions = data.seeded.map((s) => `${s.name} (${s.action})`).join(", ");
      toast.success(`Seeded ${data.count}: ${actions}`);
      load();
    } catch (err) { toast.error(extractError(err)); }
    finally { setSeeding(false); }
  };

  const resetUserPassword = async (userId, label) => {
    const ok = window.confirm(`Generate a temporary password for ${label}?\n\nYou'll see the password ONCE — copy it and share it with them via text, call, or DM. They should change it on first login.`);
    if (!ok) return;
    try {
      const { data } = await api.post(`/admin/users/${userId}/reset-password`);
      // Copy to clipboard automatically
      try { await navigator.clipboard.writeText(data.temporary_password); } catch (e) {}
      // Show it prominently in a modal-alert
      window.alert(
        `Temporary password for ${data.email}:\n\n` +
        `        ${data.temporary_password}\n\n` +
        `(already copied to your clipboard)\n\n` +
        `Share this with them. They can change it after logging in via Account Settings.`
      );
      toast.success("Password reset — copied to clipboard");
    } catch (err) { toast.error(extractError(err)); }
  };

  return (
    <div className="min-h-screen bg-stone2-50" data-testid="admin-dashboard">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-10">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <span className="section-eyebrow">Admin console</span>
            <h1 className="mt-2 text-3xl lg:text-4xl font-heading font-bold text-sage-900 flex items-center gap-2">
              <ShieldCheck className="w-7 h-7 text-sage-600" /> Trust & Safety
            </h1>
            <p className="text-stone2-600 mt-1">Review sitters, approve verifications, view uploaded documents.</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <Link
              to="/admin/blog"
              data-testid="admin-open-blog"
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg border border-sage-300 text-sage-800 hover:bg-sage-50"
              title="Write and manage blog posts"
            >
              <FileText className="w-3.5 h-3.5" />
              Blog editor
            </Link>
            <Link
              to="/admin/invoices"
              data-testid="admin-open-invoices"
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg border border-sage-300 text-sage-800 hover:bg-sage-50"
              title="Send Stripe-hosted invoices"
            >
              <DollarSign className="w-3.5 h-3.5" />
              Invoices
            </Link>
            <button
              type="button"
              onClick={seedCurated}
              disabled={seeding}
              data-testid="admin-seed-curated"
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg border border-sage-300 text-sage-800 hover:bg-sage-50 disabled:opacity-50"
              title="Ensure Maya R. and Jordan K. exist as sample verified sitters"
            >
              {seeding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserCircle2 className="w-3.5 h-3.5" />}
              Seed sample sitters
            </button>
            <button
              type="button"
              onClick={purgeTestData}
              disabled={purging}
              data-testid="admin-purge-test-data"
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg border border-red-200 text-red-700 hover:bg-red-50 disabled:opacity-50"
              title="Delete every family/sitter account whose email is on @test.com or @t.com"
            >
              {purging ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
              Purge test fixtures
            </button>
          </div>
        </div>

        {stats && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 mt-8">
            <StatCard label="Total sitters" value={stats.total_sitters} testId="admin-stat-total-sitters" />
            <StatCard label="Verified" value={stats.verified_sitters} testId="admin-stat-verified" />
            <StatCard label="Pending review" value={stats.pending_verification} tone="ochre" testId="admin-stat-pending" />
            <StatCard label="Total families" value={stats.total_families} testId="admin-stat-total-families" />
            <StatCard label="Paid bookings" value={stats.paid_bookings} testId="admin-stat-paid" />
          </div>
        )}

        {/* Tab switcher */}
        <div className="mt-8 flex items-center gap-2 border-b border-stone2-200">
          <button
            type="button"
            onClick={() => { setTab("sitters"); setOpenId(null); setDetail(null); }}
            data-testid="admin-tab-sitters"
            className={`inline-flex items-center gap-2 px-4 py-3 -mb-px text-sm font-semibold border-b-2 transition-colors ${
              tab === "sitters" ? "border-sage-600 text-sage-900" : "border-transparent text-stone2-500 hover:text-sage-800"
            }`}
          >
            <GraduationCap className="w-4 h-4" /> Sitters
            <span className="ml-1 text-xs font-normal text-stone2-500">({stats?.total_sitters ?? sitters.length})</span>
          </button>
          <button
            type="button"
            onClick={() => { setTab("families"); setOpenId(null); setDetail(null); }}
            data-testid="admin-tab-families"
            className={`inline-flex items-center gap-2 px-4 py-3 -mb-px text-sm font-semibold border-b-2 transition-colors ${
              tab === "families" ? "border-sage-600 text-sage-900" : "border-transparent text-stone2-500 hover:text-sage-800"
            }`}
          >
            <Users className="w-4 h-4" /> Families
            <span className="ml-1 text-xs font-normal text-stone2-500">({families.length})</span>
          </button>
          <button
            type="button"
            onClick={() => { setTab("invites"); setOpenId(null); setDetail(null); }}
            data-testid="admin-tab-invites"
            className={`inline-flex items-center gap-2 px-4 py-3 -mb-px text-sm font-semibold border-b-2 transition-colors ${
              tab === "invites" ? "border-sage-600 text-sage-900" : "border-transparent text-stone2-500 hover:text-sage-800"
            }`}
          >
            <Send className="w-4 h-4" /> Invites
            <span className="ml-1 text-xs font-normal text-stone2-500">({invites.length})</span>
          </button>
          <button
            type="button"
            onClick={() => { setTab("waitlist"); setOpenId(null); setDetail(null); }}
            data-testid="admin-tab-waitlist"
            className={`inline-flex items-center gap-2 px-4 py-3 -mb-px text-sm font-semibold border-b-2 transition-colors ${
              tab === "waitlist" ? "border-sage-600 text-sage-900" : "border-transparent text-stone2-500 hover:text-sage-800"
            }`}
          >
            <Bell className="w-4 h-4" /> Waitlist
            <span className="ml-1 text-xs font-normal text-stone2-500">({waitlist.results.length})</span>
          </button>
        </div>

        {/* Filters + search */}
        {tab !== "invites" && tab !== "waitlist" && (
        <div className="mt-6 flex items-center gap-3 flex-wrap">
          {tab === "sitters" && ["pending", "verified", "all"].map((f) => (
            <button key={f} type="button" onClick={() => setFilter(f)}
              data-testid={`admin-filter-${f}`}
              className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors duration-200 ${
                filter === f ? "bg-sage-600 text-white border-sage-600" : "bg-white text-sage-800 border-stone2-200 hover:border-sage-400"
              }`}>
              {f === "pending" ? "Pending review" : f === "verified" ? "Verified" : "All sitters"}
            </button>
          ))}
          <div className="ml-auto relative">
            <Search className="w-4 h-4 absolute left-3 top-2.5 text-stone2-400" />
            <input value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder={tab === "sitters" ? "Search by name or email" : "Search by name, email, or city"}
              data-testid="admin-search-input"
              className="pl-9 pr-4 py-2 rounded-lg border border-stone2-200 bg-white text-sm w-72" />
          </div>
        </div>
        )}

        {tab === "waitlist" ? (
          <div className="mt-6 grid lg:grid-cols-4 gap-6" data-testid="admin-waitlist-panel">
            {/* Top ZIPs sidebar */}
            <div className="lg:col-span-1 paper-card p-5 h-fit">
              <div className="font-heading font-semibold text-sage-900">Top ZIPs waiting</div>
              <p className="text-xs text-stone2-500 mt-1">Where to focus sitter recruitment next.</p>
              {waitlist.top_zips.length === 0 ? (
                <p className="mt-4 text-sm text-stone2-500">No signups yet.</p>
              ) : (
                <ul className="mt-4 space-y-2" data-testid="admin-waitlist-top-zips">
                  {waitlist.top_zips.map((z) => (
                    <li key={z.zip_code} className="flex items-center justify-between text-sm">
                      <span className="font-mono text-sage-900">{z.zip_code}</span>
                      <span className="text-stone2-500">{z.count} {z.count === 1 ? "family" : "families"}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {/* Waitlist entries */}
            <div className="lg:col-span-3 paper-card overflow-hidden">
              <div className="px-5 py-3 border-b border-stone2-200 flex items-center justify-between">
                <div className="font-heading font-semibold text-sage-900">All waitlist signups</div>
                <div className="text-xs text-stone2-500">{waitlist.results.length} total</div>
              </div>
              {waitlist.results.length === 0 ? (
                <div className="p-10 text-center text-stone2-500 text-sm">
                  No families are on the coverage waitlist yet.
                  <div className="mt-2 text-xs">The signup form lives on the landing page and on the empty-search state.</div>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="admin-waitlist-table">
                    <thead className="bg-sage-50 text-sage-900">
                      <tr>
                        <th className="text-left px-4 py-3 font-heading font-semibold">Email</th>
                        <th className="text-left px-4 py-3 font-heading font-semibold">ZIP</th>
                        <th className="text-left px-4 py-3 font-heading font-semibold">Name</th>
                        <th className="text-left px-4 py-3 font-heading font-semibold">Notes</th>
                        <th className="text-left px-4 py-3 font-heading font-semibold">Source</th>
                        <th className="text-left px-4 py-3 font-heading font-semibold">Joined</th>
                        <th className="text-left px-4 py-3 font-heading font-semibold">Status</th>
                        <th className="text-right px-4 py-3 font-heading font-semibold">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-stone2-100">
                      {waitlist.results.map((w) => (
                        <tr key={w.id} className="hover:bg-sage-50/60" data-testid={`admin-waitlist-row-${w.id}`}>
                          <td className="px-4 py-3">
                            <a href={`mailto:${w.email}`} className="text-sage-700 hover:text-sage-900 inline-flex items-center gap-1.5">
                              <Mail className="w-3.5 h-3.5" /> {w.email}
                            </a>
                          </td>
                          <td className="px-4 py-3 font-mono text-sage-900">{w.zip_code}</td>
                          <td className="px-4 py-3 text-stone2-700">{w.name || "—"}</td>
                          <td className="px-4 py-3 text-stone2-600 text-xs max-w-xs">{w.notes || "—"}</td>
                          <td className="px-4 py-3">
                            <span className="chip">{w.source || "landing"}</span>
                          </td>
                          <td className="px-4 py-3 text-stone2-500 text-xs whitespace-nowrap">
                            {new Date(w.created_at).toLocaleDateString()}
                          </td>
                          <td className="px-4 py-3 text-xs whitespace-nowrap">
                            {w.notified_at ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 font-medium" data-testid={`admin-waitlist-notified-${w.id}`}>
                                Notified {new Date(w.notified_at).toLocaleDateString()}
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 font-medium">
                                Waiting
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right">
                            {!w.notified_at && (
                              <button
                                onClick={() => pingWaitlistEntry(w.id)}
                                className="text-xs px-2.5 py-1 rounded-md bg-sage-600 text-white hover:bg-sage-700 transition-colors"
                                data-testid={`admin-waitlist-ping-${w.id}`}
                                title="Force-ping this family with any verified sitter now in their ZIP"
                              >
                                Ping now
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        ) : tab === "invites" ? (
          <div className="mt-6 grid lg:grid-cols-5 gap-6" data-testid="admin-invites-panel">
            {/* Compose */}
            <form onSubmit={submitInvite} className="lg:col-span-2 paper-card p-5 space-y-4 h-fit" data-testid="admin-invite-form">
              <div>
                <h3 className="font-heading font-bold text-sage-900 text-lg">Send an invite</h3>
                <p className="text-xs text-stone2-500 mt-1">
                  Creates a personalized signup link. Copy it and share via any channel, or the platform will queue email/SMS delivery once those are unblocked.
                </p>
              </div>

              <div className="flex gap-1 rounded-lg border border-stone2-200 p-1 bg-stone2-50">
                {["single", "batch"].map((m) => (
                  <button key={m} type="button" onClick={() => setInviteMode(m)}
                    data-testid={`admin-invite-mode-${m}`}
                    className={`flex-1 py-1.5 text-xs font-semibold rounded-md transition-colors ${
                      inviteMode === m ? "bg-white text-sage-900 shadow-sm" : "text-stone2-500"
                    }`}>
                    {m === "single" ? "One person" : "Bulk paste"}
                  </button>
                ))}
              </div>

              <div>
                <label className="field-label">Their role</label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { value: "family", icon: <Users className="w-4 h-4" />, label: "Family" },
                    { value: "sitter", icon: <GraduationCap className="w-4 h-4" />, label: "Sitter" },
                  ].map((r) => (
                    <button key={r.value} type="button" onClick={() => setInviteRole(r.value)}
                      data-testid={`admin-invite-role-${r.value}`}
                      className={`inline-flex items-center gap-2 py-2 rounded-lg text-sm font-semibold border transition-colors ${
                        inviteRole === r.value
                          ? "border-sage-600 bg-sage-50 text-sage-900"
                          : "border-stone2-200 bg-white text-stone2-600 hover:border-sage-300"
                      }`}>
                      {r.icon} {r.label}
                    </button>
                  ))}
                </div>
              </div>

              {inviteMode === "single" ? (
                <>
                  <div>
                    <label className="field-label">Name <span className="text-stone2-400 font-normal">(optional)</span></label>
                    <input type="text" value={singleName}
                      onChange={(e) => setSingleName(e.target.value)}
                      data-testid="admin-invite-name"
                      placeholder="Jane Smith"
                      className="w-full rounded-lg border border-stone2-200 bg-white px-3 py-2 text-sm focus:ring-2 focus:ring-sage-500" />
                  </div>
                  <div>
                    <label className="field-label">Email</label>
                    <input type="email" value={singleEmail}
                      onChange={(e) => setSingleEmail(e.target.value)}
                      data-testid="admin-invite-email"
                      placeholder="jane@example.com"
                      className="w-full rounded-lg border border-stone2-200 bg-white px-3 py-2 text-sm focus:ring-2 focus:ring-sage-500" />
                  </div>
                  <div>
                    <label className="field-label">Phone <span className="text-stone2-400 font-normal">(optional)</span></label>
                    <input type="tel" value={singlePhone}
                      onChange={(e) => setSinglePhone(e.target.value)}
                      data-testid="admin-invite-phone"
                      placeholder="(770) 555-1234"
                      className="w-full rounded-lg border border-stone2-200 bg-white px-3 py-2 text-sm focus:ring-2 focus:ring-sage-500" />
                  </div>
                </>
              ) : (
                <div>
                  <label className="field-label">Recipients — one per line</label>
                  <textarea value={batchText}
                    onChange={(e) => setBatchText(e.target.value)}
                    data-testid="admin-invite-batch"
                    rows={7}
                    placeholder={"Jane Smith <jane@example.com>\nBob, bob@x.com, +17705551234\ncarol@y.com"}
                    className="w-full rounded-lg border border-stone2-200 bg-white px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-sage-500" />
                  <p className="mt-1 text-xs text-stone2-500">
                    Accepted formats per line: <code>email@x.com</code>, <code>Name &lt;email@x.com&gt;</code>, or <code>Name, email, phone</code>
                  </p>
                </div>
              )}

              <div>
                <label className="field-label">Personal note <span className="text-stone2-400 font-normal">(optional)</span></label>
                <textarea value={inviteMessage}
                  onChange={(e) => setInviteMessage(e.target.value)}
                  data-testid="admin-invite-message"
                  rows={3}
                  maxLength={500}
                  placeholder="Hi Jane — I met you at the ARC event last week and thought you'd be a perfect fit."
                  className="w-full rounded-lg border border-stone2-200 bg-white px-3 py-2 text-sm focus:ring-2 focus:ring-sage-500" />
              </div>

              <button type="submit" disabled={inviting} className="btn-primary w-full justify-center" data-testid="admin-invite-submit">
                {inviting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Create {inviteMode === "single" ? "invite" : "invites"}
              </button>
            </form>

            {/* List */}
            <div className="lg:col-span-3 paper-card overflow-hidden">
              <div className="px-5 py-3 border-b border-stone2-200 flex items-center justify-between">
                <div className="font-heading font-semibold text-sage-900">All invites</div>
                <div className="text-xs text-stone2-500">{invites.length} total</div>
              </div>
              {invites.length === 0 ? (
                <div className="p-10 text-center text-stone2-500 text-sm">No invites yet — send one on the left.</div>
              ) : (
                <ul className="divide-y divide-stone2-100 max-h-[70vh] overflow-y-auto" data-testid="admin-invites-list">
                  {invites.map((inv) => {
                    const statusColor = {
                      pending: "bg-stone2-100 text-stone2-700",
                      opened: "bg-ochre-100 text-ochre-800",
                      converted: "bg-sage-100 text-sage-800",
                      revoked: "bg-red-100 text-red-700",
                      expired: "bg-stone2-100 text-stone2-500",
                    }[inv.status] || "bg-stone2-100";
                    return (
                      <li key={inv.id} className="p-4" data-testid={`admin-invite-row-${inv.id}`}>
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-semibold text-sage-900 truncate">
                                {inv.recipient_name || inv.recipient_email || inv.recipient_phone}
                              </span>
                              <span className={`text-[10px] uppercase tracking-wider font-bold rounded-full px-2 py-0.5 ${statusColor}`}>
                                {inv.status}
                              </span>
                              <span className="text-[10px] uppercase tracking-wider font-bold rounded-full px-2 py-0.5 bg-sage-50 text-sage-700">
                                {inv.role}
                              </span>
                            </div>
                            <div className="mt-1 text-xs text-stone2-500 flex items-center gap-3 flex-wrap">
                              {inv.recipient_email && <span><Mail className="inline w-3 h-3 mr-1" />{inv.recipient_email}</span>}
                              {inv.recipient_phone && <span><Phone className="inline w-3 h-3 mr-1" />{inv.recipient_phone}</span>}
                              <span>Created {new Date(inv.created_at).toLocaleDateString()}</span>
                            </div>
                            <div className="mt-2 flex items-center gap-1.5 text-[11px]">
                              <span className={`px-1.5 py-0.5 rounded ${inv.delivery_email_status === "sent" ? "bg-sage-50 text-sage-700" : "bg-stone2-100 text-stone2-500"}`}>
                                📧 {inv.delivery_email_status}
                              </span>
                              <span className={`px-1.5 py-0.5 rounded ${inv.delivery_sms_status === "sent" ? "bg-sage-50 text-sage-700" : "bg-stone2-100 text-stone2-500"}`}>
                                📱 {inv.delivery_sms_status}
                              </span>
                            </div>
                          </div>
                          <div className="flex flex-col gap-1.5 shrink-0">
                            <button type="button" onClick={() => copyLink(inv.url)}
                              data-testid={`admin-invite-copy-${inv.id}`}
                              className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-md bg-sage-600 text-white hover:bg-sage-700">
                              <Copy className="w-3.5 h-3.5" /> Copy link
                            </button>
                            {inv.status !== "converted" && inv.status !== "revoked" && (
                              <>
                                <button type="button" onClick={() => resendInvite(inv.id)}
                                  data-testid={`admin-invite-resend-${inv.id}`}
                                  className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-md border border-stone2-200 text-sage-800 hover:bg-sage-50">
                                  <RefreshCw className="w-3.5 h-3.5" /> Resend
                                </button>
                                <button type="button" onClick={() => revokeInvite(inv.id)}
                                  data-testid={`admin-invite-revoke-${inv.id}`}
                                  className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-md border border-red-200 text-red-700 hover:bg-red-50">
                                  <Trash2 className="w-3.5 h-3.5" /> Revoke
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                        <div className="mt-2 text-[11px] text-stone2-400 truncate flex items-center gap-1">
                          <LinkIcon className="w-3 h-3" />
                          <span className="font-mono truncate" title={inv.url}>{inv.url}</span>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        ) : tab === "families" ? (
          <div className="mt-6 paper-card overflow-hidden" data-testid="admin-families-table-wrapper">
            {visibleFamilies.length === 0 ? (
              <div className="p-10 text-center text-stone2-500 text-sm">
                No family accounts yet.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="admin-families-table">
                  <thead className="bg-sage-50 text-sage-900">
                    <tr>
                      <th className="text-left px-4 py-3 font-heading font-semibold">Family</th>
                      <th className="text-left px-4 py-3 font-heading font-semibold">Email</th>
                      <th className="text-left px-4 py-3 font-heading font-semibold">Location</th>
                      <th className="text-left px-4 py-3 font-heading font-semibold">Phone</th>
                      <th className="text-left px-4 py-3 font-heading font-semibold">Children</th>
                      <th className="text-left px-4 py-3 font-heading font-semibold">Joined</th>
                      <th className="text-left px-4 py-3 font-heading font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-stone2-100">
                    {visibleFamilies.map((f) => {
                      const displayName = f.family_name || f.parent_name || f.name || "(no name)";
                      const location = [f.city, f.state].filter(Boolean).join(", ") || "—";
                      const phone = f.phone_number || f.parent_phone || "";
                      const joined = f.created_at ? new Date(f.created_at).toLocaleDateString() : "";
                      return (
                        <tr key={f.user_id}
                            data-testid={`admin-family-row-${f.user_id}`}
                            className="hover:bg-sage-50/60">
                          <td className="px-4 py-3">
                            <Link to={`/admin/families/${f.user_id}`}
                              data-testid={`admin-family-open-${f.user_id}`}
                              className="flex items-center gap-3 group">
                              <div className="w-8 h-8 rounded-full bg-sage-600 text-white flex items-center justify-center font-heading font-bold text-xs">
                                {displayName[0]?.toUpperCase() || "?"}
                              </div>
                              <div className="min-w-0">
                                <div className="font-semibold text-sage-900 truncate group-hover:text-sage-600 group-hover:underline">
                                  {displayName}
                                </div>
                                {!f.has_profile && (
                                  <div className="text-[10px] uppercase tracking-wider text-ochre-700 font-bold">
                                    Profile not completed
                                  </div>
                                )}
                              </div>
                            </Link>
                          </td>
                          <td className="px-4 py-3">
                            <a href={`mailto:${f.email}`} className="text-sage-700 hover:text-sage-900 inline-flex items-center gap-1.5">
                              <Mail className="w-3.5 h-3.5" /> {f.email}
                            </a>
                          </td>
                          <td className="px-4 py-3 text-stone2-700">
                            <span className="inline-flex items-center gap-1.5">
                              <MapPin className="w-3.5 h-3.5 text-stone2-400" /> {location}
                              {f.zip_code && <span className="text-stone2-400">· {f.zip_code}</span>}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            {phone ? (
                              <a href={`tel:${phone}`} className="text-sage-700 hover:text-sage-900 inline-flex items-center gap-1.5">
                                <Phone className="w-3.5 h-3.5" /> {phone}
                                {f.sms_opt_in && <span className="text-[10px] uppercase tracking-wider text-sage-700 font-bold">SMS✓</span>}
                              </a>
                            ) : <span className="text-stone2-400">—</span>}
                          </td>
                          <td className="px-4 py-3 text-stone2-700">
                            {f.child_count > 0 ? (
                              <span className="chip">{f.child_count} {f.child_count === 1 ? "child" : "children"}</span>
                            ) : (
                              <span className="text-stone2-400">0</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-stone2-600 text-xs whitespace-nowrap">{joined}</td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <button type="button"
                              onClick={() => resetUserPassword(f.user_id, displayName)}
                              data-testid={`admin-reset-pw-${f.user_id}`}
                              className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded-md border border-stone2-200 text-sage-800 hover:bg-sage-50">
                              <KeyRound className="w-3 h-3" /> Reset password
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : (
        <div className="mt-6 grid lg:grid-cols-5 gap-4">
          <div className="lg:col-span-2 space-y-3 max-h-[70vh] overflow-y-auto pr-1" data-testid="admin-sitter-list">
            {visible.length === 0 && (
              <div className="paper-card p-6 text-center text-stone2-500 text-sm">No sitters match this view.</div>
            )}
            {visible.map((s) => (
              <button key={s.id} type="button" onClick={() => openDetail(s.id)}
                data-testid={`admin-sitter-row-${s.id}`}
                className={`w-full text-left paper-card p-4 hover:border-sage-300 transition-colors duration-200 ${
                  openId === s.id ? "border-sage-500 bg-sage-50" : ""
                }`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-sage-600 text-white flex items-center justify-center font-heading font-bold">
                      {s.name?.[0]?.toUpperCase() || "?"}
                    </div>
                    <div>
                      <div className="font-heading font-semibold text-sage-900 flex items-center gap-1">
                        {s.name || "(no name)"}
                        {s.verified && <BadgeCheck className="w-4 h-4 text-sage-600" />}
                      </div>
                      <div className="text-xs text-stone2-500">{s.user_email}</div>
                      <div className="text-xs text-stone2-500 flex items-center gap-2 mt-0.5">
                        <MapPin className="w-3 h-3" /> {s.city || "—"}, {s.state || "—"}
                        <span>· ${s.hourly_rate}/hr</span>
                      </div>
                    </div>
                  </div>
                  <span className={`text-[10px] uppercase tracking-wider font-bold rounded-full px-2 py-0.5 ${
                    s.verified ? "bg-sage-100 text-sage-800" : "bg-ochre-100 text-ochre-800"
                  }`}>
                    {s.verified ? "verified" : "pending"}
                  </span>
                </div>
              </button>
            ))}
          </div>

          <div className="lg:col-span-3">
            {!openId && (
              <div className="paper-card p-10 text-center text-stone2-500">
                <UserCircle2 className="w-10 h-10 mx-auto text-stone2-300" />
                <p className="mt-3">Select a sitter on the left to review.</p>
              </div>
            )}
            {openId && !detail && (
              <div className="paper-card p-10 text-center text-stone2-500">
                <Loader2 className="w-6 h-6 mx-auto animate-spin" />
              </div>
            )}
            {detail && (
              <article className="paper-card p-6" data-testid="admin-sitter-detail">
                <header className="flex items-center justify-between flex-wrap gap-3">
                  <div className="flex items-center gap-3">
                    <div className="w-14 h-14 rounded-full bg-sage-600 text-white flex items-center justify-center font-heading font-bold text-lg">
                      {detail.name?.[0]?.toUpperCase() || "?"}
                    </div>
                    <div>
                      <h2 className="text-xl font-heading font-bold text-sage-900 flex items-center gap-2">
                        {detail.name}
                        {detail.verified && <span className="chip"><BadgeCheck className="w-3.5 h-3.5 mr-1" /> Verified</span>}
                      </h2>
                      <div className="text-sm text-stone2-600 flex items-center gap-3 flex-wrap">
                        <span>{detail.user?.email}</span>
                        <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {detail.city}, {detail.state}</span>
                        <span className="flex items-center gap-1"><DollarSign className="w-3 h-3" /> {detail.hourly_rate}/hr</span>
                        <span className="flex items-center gap-1"><Star className="w-3 h-3 text-ochre-500 fill-ochre-500" /> {detail.rating_avg} ({detail.rating_count})</span>
                      </div>
                      <button type="button"
                        onClick={() => resetUserPassword(detail.user_id, detail.name)}
                        data-testid={`admin-reset-pw-sitter-${detail.user_id}`}
                        className="mt-2 inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-md border border-stone2-200 text-sage-800 hover:bg-sage-50">
                        <KeyRound className="w-3 h-3" /> Reset password for this sitter
                      </button>
                      <Link to={`/sitter/profile?admin_edit=${detail.id}`}
                        data-testid={`admin-edit-sitter-${detail.id}`}
                        className="mt-2 ml-2 inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-md border border-sage-300 text-sage-800 hover:bg-sage-50">
                        Edit sitter profile
                      </Link>
                    </div>
                  </div>
                </header>

                <p className="mt-4 text-sm text-stone2-700 leading-relaxed">{detail.biography || "—"}</p>

                <div className="mt-4 flex flex-wrap gap-1">
                  {(detail.certifications || []).map((c) => <span key={c} className="chip">{c}</span>)}
                </div>

                <h3 className="mt-6 font-heading font-semibold text-sage-900">Verification checklist</h3>
                <ul className="mt-2 divide-y divide-stone2-200 border border-stone2-200 rounded-lg overflow-hidden">
                  {CORE.map((v) => {
                    const done = (detail.verifications || {})[v.key];
                    return (
                      <li key={v.key} className="flex items-center justify-between px-4 py-3">
                        <div className="flex items-center gap-3">
                          <span className={`w-7 h-7 rounded-full flex items-center justify-center ${
                            done ? "bg-sage-600 text-white" : "bg-stone2-100 text-stone2-500"
                          }`}>{done ? <Check className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}</span>
                          <span className="text-sm font-semibold text-sage-900">{v.label}</span>
                        </div>
                        <div className="flex gap-2">
                          <button type="button" onClick={() => toggle(detail.id, v.key, true)} disabled={busy || done}
                            data-testid={`admin-approve-${v.key}`}
                            className="text-xs font-semibold rounded-full px-3 py-1 bg-sage-600 text-white hover:bg-sage-700 disabled:opacity-40 disabled:cursor-not-allowed">
                            Approve
                          </button>
                          <button type="button" onClick={() => toggle(detail.id, v.key, false)} disabled={busy || !done}
                            data-testid={`admin-revoke-${v.key}`}
                            className="text-xs font-semibold rounded-full px-3 py-1 border border-stone2-300 text-stone2-700 hover:bg-stone2-100 disabled:opacity-40 disabled:cursor-not-allowed">
                            Revoke
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>

                <h3 className="mt-6 font-heading font-semibold text-sage-900 flex items-center gap-2">
                  <FileText className="w-4 h-4" /> Uploaded documents ({detail.files?.length || 0})
                </h3>
                {(detail.files || []).length === 0 && (
                  <p className="text-sm text-stone2-500 mt-2">No documents uploaded by this sitter yet.</p>
                )}
                <ul className="mt-2 grid sm:grid-cols-2 gap-2">
                  {(detail.files || []).map((f) => {
                    const url = `${BACKEND_URL}/api/uploads/${f.storage_path}`;
                    const isImg = (f.content_type || "").startsWith("image/");
                    return (
                      <li key={f.id} className="rounded-lg border border-stone2-200 p-3 bg-white">
                        <div className="flex items-center justify-between gap-2">
                          <div className="min-w-0">
                            <div className="text-sm font-semibold text-sage-900 truncate" title={f.original_filename}>
                              {f.original_filename || f.id}
                            </div>
                            <div className="text-xs text-stone2-500">{f.content_type} · {Math.round((f.size || 0) / 1024)} KB</div>
                          </div>
                          <a href={url} target="_blank" rel="noreferrer"
                            data-testid={`admin-view-file-${f.id}`}
                            className="text-xs font-semibold text-sage-700 hover:text-sage-900 flex items-center gap-1">
                            Open <ExternalLink className="w-3 h-3" />
                          </a>
                        </div>
                        {isImg && (
                          <img src={url} alt="" className="mt-2 rounded-lg max-h-40 w-full object-cover"
                            onError={(e) => { e.currentTarget.style.display = "none"; }} />
                        )}
                      </li>
                    );
                  })}
                </ul>

                {detail.verifications?.last_admin_review_by && (
                  <p className="mt-4 text-xs text-stone2-500">
                    Last reviewed by {detail.verifications.last_admin_review_by} · {new Date(detail.verifications.last_admin_review_at).toLocaleString()}
                  </p>
                )}
              </article>
            )}
          </div>
        </div>
        )}
      </div>
    </div>
  );
}
